import { URL_REGEX } from '@mm/constants/validations';
import ConnectionModel from '@mm/state/model/ConnectionModel';
import {
  ConnectionVariableKind,
  ConnectionVariableModel,
} from '@mm/state/model/ConnectionVariableModel';
import { getUniqId } from '@mm/utils';
import AttributeModel from '../model/AttributeModel';
import { ConnectionTagModel } from '../model/ConnectionTagModel';
import { isEmpty } from 'lodash';

const accountAuthErrorsList = ['SMTPAuthenticationError', 'invalid_grant'];

export const getEmailAccountsWithErrors = accounts =>
  accounts.filter(account =>
    account.accountErrors
      .map(err => err.errorDetails)
      .some(fullError =>
        accountAuthErrorsList.some(errorSubstring =>
          fullError.includes(errorSubstring),
        ),
      ),
  );

export const isValidURL = (connection?: Partial<ConnectionModel>): boolean => {
  if (connection && connection.url) {
    const result = connection.url.match(URL_REGEX);
    return Boolean(result);
  }

  return false;
};

export const blankVariable = (
  kind: ConnectionVariableKind = ConnectionVariableKind.toSend,
  isStatic: boolean = false,
): ConnectionVariableModel => ({
  name: '',
  sentAs: '',
  receivedAs: '',
  static: isStatic,
  id: getUniqId(),
  createdAt: '',
  system: false,
  type: '',
  kind,
});

export const canBeTested = (
  connection: ConnectionModel,
  native: boolean = false,
): boolean => {
  const {
    googleAccountId,
    googleSpreadsheetId,
    googleSheetId,
    googleLookupColumn,
  } = connection;

  const nativeVarsSet =
    googleAccountId &&
    googleSpreadsheetId &&
    googleSheetId &&
    googleLookupColumn;

  const hasJSON =
    !isEmpty(connection.requestBody) || !isEmpty(connection.requestHeaders);

  if (hasJSON) {
    return !!(connection || nativeVarsSet) && isValidURL(connection);
  }

  const isInvalidConnection =
    !connection || !connection.variables || connection.variables.length === 0;

  const variablesToSend = connection?.variables
    ? connection.variables.filter(
        variable => variable.kind !== ConnectionVariableKind.toReceive,
      )
    : [];

  const hasAttributes =
    variablesToSend.length > 0 &&
    variablesToSend[0].name &&
    variablesToSend[0].sentAs;

  const isConnectionValid = native
    ? Boolean(nativeVarsSet && !isInvalidConnection)
    : !isInvalidConnection && isValidURL(connection);

  return !!hasAttributes && isConnectionValid;
};

export const canBeTestedMailchimp = (
  connection: ConnectionModel,
  native: boolean = false,
): boolean => {
  const { mailchimpAccessTokenId, data, variables } = connection;
  const hasEMAIL = variables?.map(v => v.sentAs).includes('EMAIL');

  const nativeVarsSet = mailchimpAccessTokenId && data?.listId && hasEMAIL;

  const isConnectionValid = native
    ? Boolean(nativeVarsSet)
    : isValidURL(connection);

  return Boolean(isConnectionValid);
};

export const tagsFormatOptions = [
  { id: 'object', name: 'Object' },
  { id: 'array', name: 'Array' },
];

export const filteredAttirbutes = (
  attributes: AttributeModel[],
  currentVariables: ConnectionVariableModel[],
) => {
  const currentVariablesNames = currentVariables.map(variable => variable.name);
  return attributes.filter(
    attr =>
      !attr.system &&
      attr.name !== 'UNSUBSCRIBED' &&
      !currentVariablesNames.includes(attr.name),
  );
};

export const httpHeaderVariable = {
  ...blankVariable(),
  static: true,
  header: true,
};

export const createConnectionTag = (name: string = ''): ConnectionTagModel => ({
  name,
  sentAs: name,
});
